import { z } from "zod";
import { instantSchema, plural } from "@amber/contracts";
import { platformTableSchema } from "@amber/collection/contracts";

export const HOME_FRAMES = ["24h", "7d", "30d"] as const;
export const homeFrameSchema = z.enum(HOME_FRAMES);
export type HomeFrame = z.infer<typeof homeFrameSchema>;

export interface StatusRules {
  readonly disruptedHours: number;
  readonly disruptedIssues: number;
  readonly withIssues: number;
}

export const STARTING_STATUS_RULES: StatusRules = { disruptedHours: 6, disruptedIssues: 3, withIssues: 1 };

export type StatusIssueKind = "platform" | "source" | "required_route";

export interface StatusIssue {
  readonly kind: StatusIssueKind;
  readonly key: string;
  readonly text: string;
  readonly href: string;
  readonly failingRequiredRoute?: boolean;
}

export interface StatusPause {
  readonly text: string;
  readonly href: string;
}

export interface ServiceStatusInput {
  readonly rules: StatusRules;
  readonly scansDue: number;
  readonly scansDueNotRun: number;
  readonly acquisitionsWaiting: number;
  readonly acquisitionsFinished: number;
  readonly issues: readonly StatusIssue[];
  readonly pauses: readonly StatusPause[];
}

export const statusToneSchema = z.enum(["normal", "with_issues", "disrupted"]);
export type StatusTone = z.infer<typeof statusToneSchema>;

const linkSchema = z.strictObject({ text: z.string(), href: z.string() });

export const serviceStatusSchema = z.strictObject({
  tone: statusToneSchema,
  label: z.string(),
  issues: z.array(linkSchema.extend({ kind: z.enum(["platform", "source", "required_route"]) })),
  pauses: z.array(linkSchema),
  disrupted_because: z.array(z.string()),
});
export type ServiceStatus = z.infer<typeof serviceStatusSchema>;

function statusTone(disrupted: boolean, withIssues: boolean): StatusTone {
  if (disrupted) return "disrupted";
  return withIssues ? "with_issues" : "normal";
}

export function serviceStatus(input: ServiceStatusInput): ServiceStatus {
  const seen = new Set<string>();
  const issues = input.issues.filter((i) => (seen.has(i.key) ? false : (seen.add(i.key), true)));
  const hours = plural(input.rules.disruptedHours, "hour");
  const because: string[] = [];
  if (input.scansDue > 0 && input.scansDueNotRun * 2 > input.scansDue)
    because.push(
      `${String(input.scansDueNotRun)} of ${String(input.scansDue)} scans due in the last ${hours} have not run`,
    );
  if (input.acquisitionsWaiting > 0 && input.acquisitionsFinished === 0)
    because.push(
      `${plural(input.acquisitionsWaiting, "item")} waiting to be downloaded or archived and none finished in the last ${hours}`,
    );
  if (issues.some((i) => i.failingRequiredRoute === true)) because.push("A required route is failing");
  if (issues.length >= input.rules.disruptedIssues) because.push(`${plural(issues.length, "issue")} at once`);
  const tone = statusTone(because.length > 0, issues.length >= input.rules.withIssues);
  const label: Record<StatusTone, string> = {
    disrupted: "Disrupted",
    with_issues: `Running, with ${plural(issues.length, "issue")}`,
    normal: "Running normally",
  };
  return {
    tone,
    label: label[tone],
    issues: issues.map((i) => ({ kind: i.kind, text: i.text, href: i.href })),
    pauses: input.pauses.map((p) => ({ text: p.text, href: p.href })),
    disrupted_because: because,
  };
}

const countLinkSchema = z.strictObject({ count: z.number().int().nonnegative(), href: z.string() });
const moneyNumber = z.number();

export const flowKeySchema = z.enum(["found", "archived", "finished", "eligible"]);

export const budgetLineSchema = z.strictObject({
  key: z.string(),
  name: z.string(),
  covers: z.string(),
  allowance: moneyNumber,
  incurred: moneyNumber,
  committed: moneyNumber,
  forecast: moneyNumber,
  percent: z.number().int().nonnegative(),
  thresholds: z.strictObject({ alert: z.number(), optional: z.number() }),
  over: z.boolean(),
  state: z.string().nullable(),
  reserve: z.strictObject({ allowance: moneyNumber, used: moneyNumber }),
});
export type BudgetLine = z.infer<typeof budgetLineSchema>;

export const homePersonSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  role: z.string().nullable(),
  eligible: countLinkSchema,
  daily: z.array(z.number().int().nonnegative()),
  sources: z.strictObject({
    active: z.number().int(),
    delivering: z.number().int(),
    summary: z.string(),
    problems: z.array(z.strictObject({ title: z.string(), state: z.string(), reason: z.string().nullable() })),
  }),
  required: z.array(
    z.strictObject({
      title: z.string(),
      state: z.string(),
      ok: z.boolean(),
      text: z.string(),
      complete_through: instantSchema.nullable(),
    }),
  ),
  open_gaps: countLinkSchema,
  has_problems: z.boolean(),
});

export const homeResponseSchema = z.strictObject({
  frame: homeFrameSchema,
  generated_at: instantSchema,
  day_one: z.boolean(),
  status: serviceStatusSchema,
  rules: z.strictObject({ disrupted_hours: z.number(), disrupted_issues: z.number(), with_issues: z.number() }),
  delivery: z
    .strictObject({
      eligible: z.strictObject({
        count: z.number().int().nonnegative(),
        href: z.string(),
        previous: z.number().int().nonnegative(),
        finished: z.number().int().nonnegative(),
        draft: z.number().int().nonnegative(),
        daily: z.array(
          z.strictObject({ day: z.string(), count: z.number().int().nonnegative(), in_frame: z.boolean() }),
        ),
        policy: z.strictObject({ name: z.string(), version: z.number().int(), provisional: z.boolean() }).nullable(),
      }),
      on_time: z.strictObject({
        sample: z.number().int().nonnegative(),
        enough: z.boolean(),
        p95_hours: z.number().nullable(),
        target_hours: z.number(),
        stages: z.array(
          z.strictObject({
            key: z.string(),
            label: z.string(),
            in_stage_hours: z.number().nullable(),
            from_discovery_hours: z.number().nullable(),
            target_hours: z.number(),
            met: z.boolean().nullable(),
          }),
        ),
        past_target: z.strictObject({
          count: z.number().int().nonnegative(),
          href: z.string(),
          oldest_hours: z.number().nullable(),
          items: z.array(
            z.strictObject({
              id: z.string(),
              title: z.string(),
              stage: z.string(),
              age_hours: z.number(),
              target_hours: z.number(),
              people: z.string(),
            }),
          ),
        }),
        paused_note: z.string().nullable(),
      }),
      sources: z.strictObject({
        active: z.number().int().nonnegative(),
        delivering: z.number().int().nonnegative(),
        not_delivering: countLinkSchema,
        paused: z.number().int().nonnegative(),
        late: z.number().int().nonnegative(),
        problems: z.array(
          z.strictObject({
            id: z.string(),
            title: z.string(),
            people: z.string(),
            state: z.string(),
            reason: z.string().nullable(),
          }),
        ),
      }),
      flow: z.strictObject({
        steps: z.array(countLinkSchema.extend({ key: flowKeySchema, label: z.string() })),
        not_through: z.array(countLinkSchema.extend({ key: z.string(), label: z.string() })),
      }),
    })
    .nullable(),
  sustainability: z.strictObject({
    month: z.strictObject({ label: z.string(), day: z.number().int(), days: z.number().int() }),
    total: budgetLineSchema.nullable(),
    categories: z.array(budgetLineSchema),
    cost_per_eligible: z.strictObject({
      this_month: moneyNumber.nullable(),
      previous_month: moneyNumber.nullable(),
      eligible_this_month: z.number().int(),
      spend_this_month: moneyNumber,
    }),
    archive: z.strictObject({
      size_bytes: z.number().nonnegative(),
      deleted_contributing: z.number().int().nonnegative(),
      growth_bytes_per_month: z.number().nonnegative(),
      largest_platform: z.string().nullable(),
      href: z.string(),
    }),
    human_work: z.strictObject({
      corrections: z.number().int().nonnegative(),
      source_decisions: z.number().int().nonnegative(),
      plan_changes: z.number().int().nonnegative(),
      other_decisions: z.number().int().nonnegative(),
      waiting: countLinkSchema.extend({
        oldest_days: z.number().int().nullable(),
        other_roles: z.number().int().nonnegative(),
        by_type: z.array(
          z.strictObject({
            type: z.string(),
            label: z.string(),
            needs: z.string(),
            count: z.number().int(),
            oldest_days: z.number().int(),
          }),
        ),
      }),
    }),
  }),
  platforms: platformTableSchema,
  coverage: z.strictObject({
    open_gaps: countLinkSchema,
    oldest_days: z.number().int().nullable(),
    by_reason: z.array(z.strictObject({ label: z.string(), count: z.number().int() })),
    longest: z.strictObject({ days: z.number(), from: instantSchema, to: instantSchema }).nullable(),
    need_someone: z.number().int().nonnegative(),
    extension: z.strictObject({ text: z.string(), href: z.string() }).nullable(),
  }),
  people: z.strictObject({ rows: z.array(homePersonSchema), more: z.number().int().nonnegative() }),
  activity: z.strictObject({ in_progress: z.number().int().nonnegative(), waiting: z.number().int().nonnegative() }),
});
export type HomeResponse = z.infer<typeof homeResponseSchema>;

export const FUNNEL_BREAKDOWNS = ["person", "platform", "language", "family"] as const;
export const funnelBreakdownSchema = z.enum(FUNNEL_BREAKDOWNS);
export type FunnelBreakdown = z.infer<typeof funnelBreakdownSchema>;
export const FUNNEL_COLUMNS = [
  "found",
  "archived",
  "finished",
  "context",
  "attribution",
  "eligible",
  "not_through",
] as const;
export type FunnelColumn = (typeof FUNNEL_COLUMNS)[number];

export const homeQuerySchema = z.strictObject({ frame: homeFrameSchema.optional() });
export const funnelQuerySchema = z.strictObject({
  frame: homeFrameSchema.optional(),
  by: funnelBreakdownSchema.optional(),
});

const funnelCellsSchema = z.strictObject(
  // Safe: Object.fromEntries drops the key type, and the keys are exactly FUNNEL_COLUMNS.
  Object.fromEntries(FUNNEL_COLUMNS.map((c) => [c, countLinkSchema])) as Record<FunnelColumn, typeof countLinkSchema>,
);

export const funnelResponseSchema = z.strictObject({
  frame: homeFrameSchema,
  by: funnelBreakdownSchema,
  rows: z.array(z.strictObject({ key: z.string(), label: z.string(), cells: funnelCellsSchema })),
  total: funnelCellsSchema,
  note: z.string(),
});
export type FunnelResponse = z.infer<typeof funnelResponseSchema>;
